import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend
} from 'chart.js';
import {
  Box, Container, Typography, Select, MenuItem,
  FormControl, InputLabel, Button, CircularProgress, Grid
} from '@mui/material';

Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const API_URL = process.env.REACT_APP_API_URL || '';

const COLORS = [
  'rgba(0,203,160,0.75)', 'rgba(74,144,217,0.75)', 'rgba(244,162,97,0.75)',
  'rgba(231,111,81,0.75)', 'rgba(42,157,143,0.75)', 'rgba(233,196,106,0.75)',
  'rgba(138,176,204,0.75)',
];

const theme = {
  bg:          '#04091a',
  cardBg:      '#071525',
  cardBg2:     '#050e1e',
  text:        '#B8F1FF',
  textMuted:   '#3a5a80',
  textSub:     '#8ab0cc',
  borderCard:  'rgba(40,120,255,0.28)',
  shadow:      '0 8px 32px rgba(0,0,0,0.85), 0 2px 8px rgba(0,0,0,0.6), inset 0 1px 0 rgba(60,140,255,0.18)',
  shadowInset: 'inset 0 3px 12px rgba(0,0,0,0.75), inset 0 1px 4px rgba(0,0,0,0.6)',
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    color: theme.text,
    '& fieldset': { borderColor: theme.borderCard },
    '&:hover fieldset': { borderColor: '#4a90d9' },
    '&.Mui-focused fieldset': { borderColor: '#4a90d9' },
    backgroundColor: theme.cardBg2,
  },
  '& .MuiInputLabel-root': { color: theme.textSub },
  '& .MuiInputLabel-root.Mui-focused': { color: '#4a90d9' },
  '& .MuiSelect-icon': { color: theme.textSub },
};

const today = new Date().toISOString().split('T')[0];
const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

const GraficaProduccionReferencia = () => {
  const [fechaInicio, setFechaInicio]   = useState(weekAgo);
  const [fechaFin, setFechaFin]         = useState(today);
  const [referencia, setReferencia]     = useState('');
  const [referencias, setReferencias]   = useState([]);
  const [chartData, setChartData]       = useState(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [totalMetros, setTotalMetros]   = useState(0);
  const [maquinas, setMaquinas]         = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/referencias`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then(r => r.json())
      .then(data => setReferencias(Array.isArray(data) ? data : []))
      .catch(() => setReferencias([]));
  }, []);

  const fetchData = async () => {
    if (!referencia) { setError('Selecciona una referencia'); return; }
    if (!fechaInicio || !fechaFin) { setError('Selecciona ambas fechas'); return; }
    setLoading(true);
    setError('');
    setChartData(null);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ fechaInicio, fechaFin, referencia });
      const res = await fetch(`${API_URL}/produccion-referencia?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('Error al obtener datos');
      const json = await res.json();
      const rows = json.data || [];

      if (rows.length === 0) {
        setError('Sin datos para el rango y referencia seleccionados.');
        setLoading(false);
        return;
      }

      // Obtener máquinas únicas
      const maquinasUnicas = [...new Set(rows.map(r => r.maquina))].sort();
      setMaquinas(maquinasUnicas);

      // Fechas únicas ordenadas
      const fechas = [...new Set(rows.map(r => r.fecha))].sort();

      // Un dataset por máquina
      const datasets = maquinasUnicas.map((maq, idx) => {
        const valores = fechas.map(f => {
          const fila = rows.find(r => r.fecha === f && r.maquina === maq);
          return fila ? Math.round(fila.produccion * 100) / 100 : 0;
        });
        return {
          label: maq,
          data: valores,
          backgroundColor: COLORS[idx % COLORS.length],
          borderColor: COLORS[idx % COLORS.length].replace('0.75', '1'),
          borderWidth: 1,
          borderRadius: 4,
        };
      });

      const total = rows.reduce((s, r) => s + (r.produccion || 0), 0);
      setTotalMetros(Math.round(total * 100) / 100);
      setChartData({ labels: fechas, datasets });
    } catch {
      setError('Error al cargar datos. Verifica la conexión.');
    } finally {
      setLoading(false);
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: '#8ab0cc', font: { size: 11 }, padding: 12 }
      },
      title: {
        display: true,
        text: referencia ? `Producción diaria — ${referencia}` : 'Producción por Referencia',
        color: '#8ab0cc',
        font: { family: 'Roboto', size: 13 },
        padding: { bottom: 10 }
      },
      tooltip: {
        callbacks: {
          label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString('es-CO')} m`
        }
      }
    },
    scales: {
      x: {
        stacked: false,
        grid: { color: 'rgba(30,90,180,0.2)', lineWidth: 0.5 },
        ticks: { color: '#8ab0cc', font: { size: 10 }, maxRotation: 45 }
      },
      y: {
        stacked: false,
        grid: { color: 'rgba(30,90,180,0.2)', lineWidth: 0.5 },
        ticks: { color: '#8ab0cc', font: { size: 10 } },
        title: { display: true, text: 'Metros producidos', color: '#8ab0cc', font: { size: 10 } },
        beginAtZero: true
      }
    }
  };

  return (
    <Box sx={{ backgroundColor: theme.bg, minHeight: '100vh', py: 3, px: { xs: 1, sm: 2 } }}>
      <Container maxWidth="xl">
        <Typography variant="h5" sx={{ color: theme.text, mb: 3, fontWeight: 600, letterSpacing: 1 }}>
          Producción por Referencia
        </Typography>

        {/* Formulario */}
        <Box sx={{
          backgroundColor: theme.cardBg,
          border: `1px solid ${theme.borderCard}`,
          borderRadius: '10px',
          boxShadow: theme.shadow,
          p: { xs: 2, sm: 3 },
          mb: 3
        }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography sx={{ color: theme.textSub, fontSize: 12, pl: 0.5 }}>Fecha Inicio</Typography>
                <Box
                  component="input"
                  type="date"
                  value={fechaInicio}
                  onChange={e => setFechaInicio(e.target.value)}
                  sx={{
                    backgroundColor: theme.cardBg2,
                    color: theme.text,
                    border: `1px solid ${theme.borderCard}`,
                    borderRadius: '6px',
                    px: 1.5,
                    py: 1.2,
                    fontSize: '1rem',
                    fontFamily: 'Roboto, sans-serif',
                    outline: 'none',
                    width: '100%',
                    cursor: 'pointer',
                    colorScheme: 'dark',
                    '&:focus': { borderColor: '#4a90d9' },
                    '&::-webkit-calendar-picker-indicator': { filter: 'invert(0.7) sepia(1) saturate(3) hue-rotate(180deg)', cursor: 'pointer' },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography sx={{ color: theme.textSub, fontSize: 12, pl: 0.5 }}>Fecha Fin</Typography>
                <Box
                  component="input"
                  type="date"
                  value={fechaFin}
                  onChange={e => setFechaFin(e.target.value)}
                  sx={{
                    backgroundColor: theme.cardBg2,
                    color: theme.text,
                    border: `1px solid ${theme.borderCard}`,
                    borderRadius: '6px',
                    px: 1.5,
                    py: 1.2,
                    fontSize: '1rem',
                    fontFamily: 'Roboto, sans-serif',
                    outline: 'none',
                    width: '100%',
                    cursor: 'pointer',
                    colorScheme: 'dark',
                    '&:focus': { borderColor: '#4a90d9' },
                    '&::-webkit-calendar-picker-indicator': { filter: 'invert(0.7) sepia(1) saturate(3) hue-rotate(180deg)', cursor: 'pointer' },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth sx={inputSx}>
                <InputLabel>Referencia</InputLabel>
                <Select
                  value={referencia}
                  onChange={e => setReferencia(e.target.value)}
                  label="Referencia"
                  MenuProps={{
                    PaperProps: {
                      sx: { backgroundColor: theme.cardBg, color: theme.text }
                    }
                  }}
                >
                  {referencias.map(r => (
                    <MenuItem key={r._id} value={r.referencia} sx={{ '&:hover': { backgroundColor: theme.cardBg2 } }}>
                      {r.referencia}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                fullWidth
                onClick={fetchData}
                disabled={loading}
                sx={{
                  backgroundColor: '#0d4f8c',
                  color: '#B8F1FF',
                  fontWeight: 600,
                  height: 56,
                  '&:hover': { backgroundColor: '#1565c0' },
                  '&.Mui-disabled': { backgroundColor: theme.cardBg2, color: theme.textMuted }
                }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : 'Consultar'}
              </Button>
            </Grid>
          </Grid>
          {error && (
            <Typography sx={{ color: '#ef5350', mt: 1.5, fontSize: 13 }}>{error}</Typography>
          )}
        </Box>

        {/* Totales */}
        {chartData && (
          <Box sx={{
            display: 'flex',
            gap: 2,
            mb: 2,
            flexWrap: 'wrap'
          }}>
            <Box sx={{
              backgroundColor: theme.cardBg,
              border: `1px solid ${theme.borderCard}`,
              borderRadius: '8px',
              boxShadow: theme.shadow,
              px: 3, py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <Typography sx={{ color: theme.textSub, fontSize: 13 }}>Total producido:</Typography>
              <Typography sx={{ color: '#00cba0', fontSize: 20, fontWeight: 700 }}>
                {totalMetros.toLocaleString('es-CO')} m
              </Typography>
            </Box>
            <Box sx={{
              backgroundColor: theme.cardBg,
              border: `1px solid ${theme.borderCard}`,
              borderRadius: '8px',
              boxShadow: theme.shadow,
              px: 3, py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <Typography sx={{ color: theme.textSub, fontSize: 13 }}>Referencia:</Typography>
              <Typography sx={{ color: theme.text, fontSize: 16, fontWeight: 600 }}>{referencia}</Typography>
            </Box>
            <Box sx={{
              backgroundColor: theme.cardBg,
              border: `1px solid ${theme.borderCard}`,
              borderRadius: '8px',
              boxShadow: theme.shadow,
              px: 3, py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <Typography sx={{ color: theme.textSub, fontSize: 13 }}>Máquinas:</Typography>
              <Typography sx={{ color: theme.text, fontSize: 14 }}>{maquinas.join(', ')}</Typography>
            </Box>
          </Box>
        )}

        {/* Gráfica */}
        {chartData && (
          <Box sx={{
            backgroundColor: theme.cardBg,
            border: `1px solid ${theme.borderCard}`,
            borderRadius: '10px',
            boxShadow: theme.shadow,
            p: 2
          }}>
            <Box sx={{
              height: { xs: 320, sm: 460 },
              backgroundColor: theme.cardBg2,
              borderRadius: '8px',
              border: `1px solid ${theme.borderCard}`,
              boxShadow: theme.shadowInset,
              p: 2
            }}>
              <Bar data={chartData} options={options} />
            </Box>
          </Box>
        )}

        {!chartData && !loading && !error && (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography sx={{ color: theme.textMuted, fontSize: 15 }}>
              Selecciona un rango de fechas y una referencia, luego pulsa Consultar
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default GraficaProduccionReferencia;
